class MadeToOrderOption extends window.BAO.CustomElement() {
  static get requiredAttributes () {
    return ['data-can-multi-select', 'data-title']
  }

  static get requiredElements () {
    return ['data', 'display-text', 'image', 'price']
  }

  selectedAttribute = 'aria-selected'

  connectedCallback () {
    super.connectedCallback()

    window.BAO.MadeToOrder.attach(this)
  }

  setupListeners () {
    super.setupListeners()

    this.listeners.add(this, 'click', this.handleClick.bind(this))
    this.listeners.add(
      document.body,
      'OptionSelected',
      this.#onOptionSelection.bind(this),
    )
  }

  /**
   * Handles the click event on a product option
   * @param {PointerEvent} event - The click event
   * @fires MadeToOrderEvents.OptionSelected
   */
  handleClick (event) {
    event.preventDefault()

    // Do nothing if it's disabled
    if (this.hasAttribute('disabled')) return

    if (this.dataset.canMultiSelect === 'true') {
      const shouldDecrement = !!event.target.closest(
        '[data-quantity-adjuster-el="minus"]',
      )
      const didClickOnIncrement = !!event.target.closest(
        '[data-quantity-adjuster-el="plus"]',
      )

      // If we're currently at the maximum number of selected options, and we're trying to increment, then we should just do nothing
      if (shouldDecrement) {
        this.productGroup.selectedOptions.decrement(this)
      } else {
        this.productGroup.selectedOptions.increment(this)

        if (!didClickOnIncrement) {
          const selectedOption = this.productGroup.selectedOptions.find(
            (selectedOption) => selectedOption.option === this,
          )
          if (selectedOption) {
            this.quantityAdjuster.value = selectedOption.quantity
          }
        }
      }
    } else {
      if (
        this.productGroup.canHaveMultipleSelections &&
        this.productGroup.selectedOptions.count >
          this.productGroup.minimumNumberOfChoices
      ) {
        this.productGroup.selectedOptions.remove(this)
      } else {
        this.productGroup.selectedOptions.add(this)
      }
    }

    const currentAmountOfSelectedOptions =
      this.productGroup.currentSelectedOptionCount
    const wasSelectedBeforeAnyChanges =
      this.getAttribute(this.selectedAttribute) === 'true'

    this.log(`${this.localName}##onClick`, {
      currentAmountOfSelectedOptions,
      wasSelectedBeforeAnyChanges,
    })
    // TODO: Why was this here to begin with?
    // if (
    //   wasSelectedBeforeAnyChanges &&
    //   currentAmountOfSelectedOptions ===
    //     this.productGroup.data.minimumNumberOfChoices
    // ) {
    //   return
    // }

    for (const option of this.productGroup.options) {
      option.setAttribute(this.selectedAttribute, 'false')
    }

    for (const selectedOption of this.productGroup.selectedOptions) {
      selectedOption.option.setAttribute(this.selectedAttribute, 'true')
    }

    this.dispatchEvent(
      new CustomEvent('OptionSelected', {
        bubbles: true,
        composed: true,
        detail: {
          selectedOption: this,
        },
      }),
    )

    if (
      this.productGroup.currentSelectedOptionCount ===
      this.productGroup.data.maximumNumberOfChoices
    ) {
      this.drawer.drawers.close()
    }
  }

  /**
   * Handles when an option is selected in a product group
   * @listens CustomEvent#OptionSelected
   * @param {CustomEvent} event - The option selected event
   * @param {Object} event.detail - The event details
   * @param {MadeToOrderOption} event.detail.selectedOption - The option element where the selection was made
   */
  #onOptionSelection (event) {
    if (event.detail.selectedOption === this) return

    const prices = this.getPrices()
    const priceAmount = this.getPriceConfigurationAmount()

    if (priceAmount != null) {
      const amount = this.madeToOrder.isB2bCustomer
        ? priceAmount * 100 * 0.7
        : priceAmount * 100

      this.els.price.element.dataset.amount = amount.toString()
      this.els.price.element.innerHTML = window.BAO.Utils.formatMoney(amount)
      this.els.price.element.dataset.priceIds = prices
        .map((price) => price.id)
        .join(',')
    }

    this.#updateRelativePrice()
  }

  #updateRelativePrice () {
    if (this.isPartOfFirstProductGroup()) return
    if (this.productGroup.data.inheritShopifyPrices) return
    if (this.productGroup.data.minimumNumberOfChoices > 1) return

    if (this.isSelected()) {
      this.els.price.element.innerHTML = window.BAO.Utils.formatMoney(0)

      return
    }

    const currentPriceForThisProductGroup = this.productGroup.getCurrentPrice()
    const myPrice = this.getPriceDatasetAmount()
    const myPriceIsCheaper = myPrice < currentPriceForThisProductGroup

    this.els.price.element.innerHTML = `<span class="prd-Price_From prd-Price_From-small">${myPriceIsCheaper ? '-' : '+'}</span>${window.BAO.Utils.formatMoney(Math.abs(myPrice - currentPriceForThisProductGroup))}`
  }

  /**
   * Gets the display text for this option
   *
   * @returns {string}
   */
  getDisplayText () {
    const baseText = this.els['display-text'].element.textContent.trim()

    if (this.isPartOfFirstProductGroup()) {
      return baseText
    }

    if (this.getPriceDatasetAmount() === 0) return baseText

    const priceText = `(+${window.BAO.Utils.formatMoney(this.getPriceDatasetAmount())})`

    return `${baseText} ${priceText}`
  }

  /**
   * Gets the images for this option
   *
   * @returns {{ main: HTMLImageElement }}
   */
  getImages () {
    return {
      main: this.els.image.element.querySelector('img'),
    }
  }

  isPartOfFirstProductGroup () {
    return (
      this.madeToOrder.productGroups.findIndex(
        (productGroup) => productGroup.key === this.productGroup.key,
      ) === 0
    )
  }

  isSelected () {
    return this.productGroup.selectedOptions.some(
      (selectedOption) => selectedOption.option === this,
    )
  }

  /**
   * @returns {string}
   */
  getImageKey () {
    return this.data.imageIdentifier
  }

  /**
   * @returns {number}
   */
  getPriceDatasetAmount () {
    return Number(this.els.price.element.dataset.amount) ?? 0
  }

  getRrpPriceDatasetAmount () {
    return Number(this.els.price.element.dataset.rrpAmount) ?? 0
  }

  getPriceIds () {
    return this.els.price.element.dataset?.priceIds?.split(',') ?? []
  }

  getTitle () {
    return this.getAttribute('data-title')
  }

  /**
   * @returns {MTOPrice[]}
   */
  getPrices () {
    const isoCode = this.madeToOrder.currencyIsoCode
    const prices = Object.values(this.data.prices[isoCode] ?? {})
    if (prices.length === 0) return []

    const relevantPrices = prices.filter(
      (price) =>
        (price.configurationId === this.madeToOrder.data.id &&
          price.currencyCode === this.madeToOrder.currencyIsoCode) ||
        (price.configurationId == null &&
          price.currencyCode === this.madeToOrder.currencyIsoCode),
    )
    if (relevantPrices.length === 0) return []

    /**
     * @returns MTOPrice[]
     */
    const getBasePrice = () => {
      const basePrice = relevantPrices.find(
        (price) =>
          price.configurationId == null &&
          price.currencyCode === this.madeToOrder.currencyIsoCode,
      )
      if (!basePrice) return []

      return [basePrice]
    }

    const basePrice = getBasePrice()

    const pricesThatHaveDependents = relevantPrices.filter(
      (price) =>
        price.dependentProductGroupId != null &&
        price.dependentProductGroupItemId != null &&
        this.madeToOrder.productGroups.some(
          (productGroup) => productGroup.key === price.dependentProductGroupId,
        ),
    )
    if (pricesThatHaveDependents.length === 0) {
      return basePrice
    }

    const dependentPrices = pricesThatHaveDependents.filter((price) => {
      const dependentProductGroup = this.madeToOrder.productGroups.find(
        (productGroup) => productGroup.key === price.dependentProductGroupId,
      )
      if (!dependentProductGroup) {
        throw new Error(
          `Unable to find product group with key ${price.dependentProductGroupId}`,
        )
      }

      const selectedItemIds = dependentProductGroup.selectedOptions.map(
        (selectedOption) => selectedOption.option.id,
      )

      return selectedItemIds.includes(price.dependentProductGroupItemId)
    })

    if (dependentPrices.length === 0) {
      return basePrice
    }

    if (
      typeof this.dependentProductGroup !== 'undefined' &&
      dependentPrices.length < this.dependentProductGroup.selectedOptions.length
    ) {
      return basePrice.concat(dependentPrices)
    }

    return dependentPrices
  }

  getPriceConfigurationAmount () {
    const prices = this.getPrices()
    if (prices.length === 0) return null

    if (typeof this.dependentProductGroup === 'undefined') {
      return prices[0].amount
    }

    return this.dependentProductGroup.selectedOptions.reduce(
      (acc, selectedOption) => {
        const price = prices.find(
          (price) =>
            price.dependentProductGroupItemId === selectedOption.option.id,
        )
        if (!price) return acc + prices[0].amount * selectedOption.quantity

        return acc + price.amount * selectedOption.quantity
      },
      0,
    )
  }

  get data () {
    return JSON.parse(this.els.data.element.textContent.trim())
  }

  get quantityAdjuster () {
    return window.BAO.Utils.getElement(`quantity-adjuster`, {
      context: this,
    })
  }
}

if (!customElements.get('made-to-order-option')) {
  customElements.define('made-to-order-option', MadeToOrderOption)
}
